import Cookies from 'js-cookie';

import { LEGACY_USER_ACCESS_TOKEN_COOKIE } from '@/shared/config/cookies.config';
import { USER_ACCESS_TOKEN_KEY } from '@/shared/config/storage.config';

/**
 * Persistence for the admin session.
 *
 * Two jobs, both of which exist because browser storage fails quietly:
 *
 *   1. The access token lives in localStorage, not a cookie — see
 *      USER_ACCESS_TOKEN_KEY in `storage.config.ts` for why the 4096-byte
 *      cookie ceiling made a cookie the wrong home for a permissions-bearing
 *      JWT.
 *   2. Every write here is read back and verified. `document.cookie` has no
 *      return value and `localStorage.setItem` only throws on quota, so a
 *      rejected write is otherwise indistinguishable from a successful one.
 *      That is precisely how the oversized-token bug stayed invisible: the
 *      cookie was never stored, nothing reported it, and the failure only
 *      surfaced one tab later as an unexplained logout.
 */

/** Browsers store a cookie only when name + value fits in this many bytes. */
export const MAX_COOKIE_NAME_VALUE_BYTES = 4096;

const LOG_PREFIX = '[AUTH][storage]';

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function localStorageOrNull(): Storage | null {
  try {
    // Accessing window.localStorage itself throws when site data is blocked,
    // so the guard has to be inside the try, not a truthiness check outside it.
    const store = window.localStorage;
    const probe = '__carofi_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return null;
  }
}

/**
 * Writes a cookie and confirms the browser actually kept it.
 *
 * Returns false — loudly — when it did not, which for an auth cookie means the
 * session will not survive a reload. `Cookies.get` returns the decoded value,
 * so comparing against the input catches both a rejected write and a stale
 * cookie of the same name left behind by one.
 */
export function setCookieChecked(
  name: string,
  value: string,
  options?: Cookies.CookieAttributes
): boolean {
  try {
    Cookies.set(name, value, options);
  } catch (error) {
    console.error(`${LOG_PREFIX} threw while writing cookie "${name}"`, error);
    return false;
  }

  if (Cookies.get(name) === value) {
    return true;
  }

  const bytes = byteLength(name) + byteLength(value);
  const oversized = bytes > MAX_COOKIE_NAME_VALUE_BYTES;
  console.error(
    `${LOG_PREFIX} cookie "${name}" was NOT stored by the browser. ` +
      `name + value = ${bytes} bytes` +
      (oversized
        ? ` — over the ${MAX_COOKIE_NAME_VALUE_BYTES}-byte per-cookie limit, so it was discarded silently.`
        : ` — under the ${MAX_COOKIE_NAME_VALUE_BYTES}-byte limit, so cookies are likely blocked for this site.`)
  );
  return false;
}

/**
 * Reads the access token, migrating any session that still keeps it in the
 * pre-move cookie. The cookie is only dropped once the localStorage copy is
 * confirmed, so an interrupted migration leaves the user signed in.
 */
export function readAccessToken(): string | undefined {
  const store = localStorageOrNull();

  if (store) {
    const stored = store.getItem(USER_ACCESS_TOKEN_KEY);
    if (stored && stored !== 'undefined') {
      return stored;
    }
  }

  const legacy = Cookies.get(LEGACY_USER_ACCESS_TOKEN_COOKIE);
  if (!legacy || legacy === 'undefined') {
    return undefined;
  }

  // writeAccessToken drops the cookie itself once the localStorage copy is
  // confirmed, so an interrupted migration just retries on the next read.
  if (store) {
    writeAccessToken(legacy);
  }
  return legacy;
}

/**
 * Persists the access token. Falls back to the old cookie when localStorage is
 * unavailable so no browser ends up worse off than before the move — but that
 * path reinstates the size ceiling, so it reports when the token cannot fit.
 */
export function writeAccessToken(accessToken: string): boolean {
  const store = localStorageOrNull();

  if (store) {
    try {
      store.setItem(USER_ACCESS_TOKEN_KEY, accessToken);
      if (store.getItem(USER_ACCESS_TOKEN_KEY) === accessToken) {
        // A leftover cookie shadows nothing, but it still lingers and gets
        // sent on every request to the origin.
        if (Cookies.get(LEGACY_USER_ACCESS_TOKEN_COOKIE) !== undefined) {
          Cookies.remove(LEGACY_USER_ACCESS_TOKEN_COOKIE);
        }
        return true;
      }
      console.error(`${LOG_PREFIX} localStorage did not retain the access token.`);
    } catch (error) {
      console.error(`${LOG_PREFIX} localStorage rejected the access token`, error);
    }
  }

  console.error(
    `${LOG_PREFIX} falling back to the legacy cookie for the access token — ` +
      'localStorage is unavailable. The session will break if the token exceeds ' +
      `${MAX_COOKIE_NAME_VALUE_BYTES} bytes.`
  );
  return setCookieChecked(LEGACY_USER_ACCESS_TOKEN_COOKIE, accessToken, { expires: 365 });
}

export function clearAccessToken(): void {
  const store = localStorageOrNull();
  try {
    store?.removeItem(USER_ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error(`${LOG_PREFIX} failed to clear the access token`, error);
  }
  Cookies.remove(LEGACY_USER_ACCESS_TOKEN_COOKIE);
}
